// @flow
import { getItemCardImageUrl, logoUrl } from '../../utils/urls';
import { injectIntl, intlShape } from 'react-intl';
import React, { Component } from 'react';
import isEqual from 'react-fast-compare';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';

type Props = $ReadOnly<{|
    archivedSearch: boolean,
    baseUrl: string,
    canonicalURL: string,
    category: any,
    categoryWithGreatestResults: any,
    firstItem: any,
    intl: intlShape,
    pageLabel: string,
    pageMetaDescription: string,
    pageMetaKeywords: string,
    pageTitle: string,
    selectedCategory: any,
    selectedCreator: any,
    selectedMaterials: any,
    selectedOrigin: any,
    selectedStyles: any,
    totalFound: number,
    totalUpcomingAuctions: number,
|}>;

class CategoryLandingPageHeaderMeta extends Component<Props> {
    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    getLdMarkup = (data: any) => {
        return <script type="application/ld+json">{JSON.stringify(data)}</script>;
    };

    getLdData = () => {
        return {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [],
        };
    };

    getCategoryLdBreadcrumbs = (selectedCategory: any, currentURL: string) => {
        if (!selectedCategory?.name) {
            return;
        }
        let categoryCount = 0;
        const ldData = this.getLdData();
        const { baseUrl } = this.props;
        ldData.itemListElement.push({
            '@type': 'ListItem',
            item: {
                '@id': `${baseUrl}`,
                name: 'Home',
            },
            position: categoryCount++,
        });
        if (selectedCategory.parents) {
            while (categoryCount <= selectedCategory.parents.length) {
                ldData.itemListElement.push({
                    '@type': 'ListItem',
                    item: {
                        '@id': `${baseUrl}/c/${selectedCategory.parents[categoryCount - 1].landingPageUrl}`,
                        name: selectedCategory.parents[categoryCount - 1].name,
                    },
                    position: categoryCount++,
                });
            }
        }
        ldData.itemListElement.push({
            '@type': 'ListItem',
            item: {
                '@id': `${currentURL}/`,
                name: selectedCategory.name,
            },
            position: categoryCount + 1,
        });
        return this.getLdMarkup(ldData);
    };

    getPriceResultsLdBreadcrumbs = (selectedCreator: any) => {
        if (!selectedCreator?.name) {
            return;
        }
        const ldData = this.getLdData();
        const { baseUrl } = this.props;
        ldData.itemListElement.push({
            '@type': 'ListItem',
            item: {
                '@id': `${baseUrl}`,
                name: 'Home',
            },
            position: 1,
        });
        ldData.itemListElement.push({
            '@type': 'ListItem',
            item: {
                '@id': `${baseUrl}/auction-results`,
                name: 'Price Results',
            },
            position: 2,
        });
        ldData.itemListElement.push({
            '@type': 'ListItem',
            item: {
                '@id': `${baseUrl}/price-guide/${selectedCreator?.seoURL}/${selectedCreator?.id}/`,
                name: selectedCreator.name,
            },
            position: 3,
        });

        return this.getLdMarkup(ldData);
    };

    render() {
        const {
            archivedSearch,
            baseUrl,
            category,
            categoryWithGreatestResults,
            firstItem,
            intl,
            pageLabel,
            pageMetaDescription,
            pageMetaKeywords,
            pageTitle,
            canonicalURL,
            selectedCategory,
            selectedCreator,
            selectedMaterials,
            selectedOrigin,
            selectedStyles,
            totalFound,
            totalUpcomingAuctions,
        } = this.props;
        const { formatMessage } = intl;
        let description = category?.description || pageMetaDescription;
        let title = pageTitle;

        const { catalogId, imageVersion, itemId, photo, photos, sellerId } = firstItem ? firstItem.item : {};

        let firstPhoto;
        if (photo) {
            firstPhoto = photo;
        } else if (photos && photos.length > 0) {
            firstPhoto = photos[0];
        } else {
            firstPhoto = 0;
        }

        const imageUrl = firstItem
            ? getItemCardImageUrl(sellerId, catalogId, itemId, firstPhoto, imageVersion)
            : logoUrl;

        if (archivedSearch && selectedCreator && selectedCreator.name) {
            title = `${selectedCreator.name} ${formatMessage({
                id: 'price_guide_title',
            })} ${totalFound.toLocaleString()} ${formatMessage({
                id: 'auction_price_results',
            })}`;
            description = `${formatMessage({
                id: 'price_guide_results_how_much',
            })} ${selectedCreator.name} ${formatMessage({ id: 'price_guide_results_worth' })} `;
            description += `${formatMessage({
                id: 'price_guide_results_research',
            })} ${totalFound.toLocaleString()} ${selectedCreator.name} ${formatMessage({
                id: 'price_guide_results_in',
            })} ${categoryWithGreatestResults.name}. `;
            description += `${formatMessage({
                id: 'price_guide_results_value_of',
            })} ${selectedCreator.name}.`;
        } else if (!archivedSearch && canonicalURL !== '/c/') {
            title = `${formatMessage({ id: 'category_meta_vintage' })} `;
            description = category?.description || `${formatMessage({ id: 'category_meta_shop_for' })} `;
            let combinedPart = ' ';

            if (totalFound === 0) {
                combinedPart = pageLabel + ' ';
                description += `${combinedPart}`;
            } else if (selectedCategory && (selectedMaterials.name || selectedStyles.name || selectedOrigin.name)) {
                combinedPart = selectedStyles.name ? `${selectedStyles.name} ` : '';
                combinedPart += selectedOrigin.name ? `${selectedOrigin.name} ` : '';
                combinedPart += selectedMaterials.name ? `${selectedMaterials.name} ` : '';
                combinedPart += `${categoryWithGreatestResults.name} `;
                description += ` ${combinedPart}`;
            } else if (!selectedCategory && (selectedMaterials.name || selectedStyles.name || selectedOrigin.name)) {
                combinedPart = selectedMaterials.name ? `${selectedMaterials.name} ` : '';
                combinedPart += selectedStyles.name ? `${selectedStyles.name} ` : '';
                combinedPart += selectedOrigin.name ? `${selectedOrigin.name} ` : '';
                description += `${categoryWithGreatestResults.name} `;
            } else {
                combinedPart = `${categoryWithGreatestResults.name} `;
                description += `${categoryWithGreatestResults.name} `;
            }

            description += formatMessage({ id: 'category_meta_at_auction' });

            if (selectedCreator.name) {
                description += ` ${formatMessage({ id: 'category_meta_from' })} ${selectedCreator.name}`;
            }

            title += `${selectedCreator.name ? selectedCreator.name + ' ' : ''}${combinedPart}${formatMessage({
                id: 'category_meta_for_sale',
            })} ${selectedCreator.name ? selectedCreator.name + ' ' : ''}${combinedPart}`;
            description += `${formatMessage({
                id: 'category_meta_starting_bids',
            })} ${totalFound} ${formatMessage({ id: 'category_meta_lots_available' })} `;

            if (selectedCreator.name) {
                description += `${selectedCreator.name} `;
            }
            description += `${combinedPart}${formatMessage({
                id: 'category_meta_and',
            })} ${totalUpcomingAuctions} `;
            description += `${formatMessage({ id: 'category_meta_miss_out' })}`;
        } else {
            title = formatMessage({ id: 'category_root_title' });
            description = category?.description || formatMessage({ id: 'category_root_description' });
        }

        const canonical = `${baseUrl}${canonicalURL}`;
        const ldBreadCrumbs = archivedSearch
            ? this.getPriceResultsLdBreadcrumbs(selectedCreator)
            : this.getCategoryLdBreadcrumbs(selectedCategory, canonical);

        return (
            <PageHeaderMeta canonical={canonical} description={description} imageUrl={imageUrl} title={title}>
                <meta name="keywords" content={pageMetaKeywords} />
                {ldBreadCrumbs}
            </PageHeaderMeta>
        );
    }
}

export default injectIntl(CategoryLandingPageHeaderMeta);
